import type { ActivityType } from './activities/types'
import {
  FollowUpScheduleError,
  FollowUpService,
  type ScheduleWorkflowFollowUpInput,
  type ScheduleWorkflowFollowUpResult,
  type UpdatePendingFollowUpInput,
} from './supabase/services/followUps.service'
import type { FollowUpRow } from './supabase/types/database.types'
import {
  WorkflowService,
  type DailyAction,
  type NextAction,
  type WorkflowActionStatus,
  type WorkflowHistoryItem,
  type WorkflowState,
} from './supabase/services/workflow.service'

export { FollowUpScheduleError }
export {
  createFollowUpActivity,
  updateProspectNextFollowUp,
  updateWorkflowAfterFollowUp,
} from './followUps/idempotency'
export type {
  DailyAction,
  NextAction,
  WorkflowActionStatus,
  WorkflowHistoryItem,
  WorkflowState,
  ScheduleWorkflowFollowUpInput,
  ScheduleWorkflowFollowUpResult,
  UpdatePendingFollowUpInput,
}

export interface WorkflowActivityInput {
  activityType: ActivityType
  description: string
  channel?: string
  result?: string
  receivedResponse?: boolean
  interestLevel?: string
  nextFollowUpAt?: string
  workflowActionType?: string
}

export interface RetryFollowUpActivityInput {
  prospectId: string
  scheduledAt: string
  channel: string
  notes?: string
  followUpReason?: string | null
  reason?: string | null
  assignedTo?: string
  profileId?: string
}

export async function scheduleWorkflowFollowUp(
  input: ScheduleWorkflowFollowUpInput,
) {
  return WorkflowService.scheduleFollowUp(input)
}

export async function retryFollowUpActivity(input: RetryFollowUpActivityInput) {
  return WorkflowService.retryFollowUpActivity(input)
}

export async function loadPendingFollowUp(
  prospectId: string,
): Promise<FollowUpRow | null> {
  return FollowUpService.getPending(prospectId)
}

export async function updatePendingFollowUp(
  input: UpdatePendingFollowUpInput,
): Promise<FollowUpRow> {
  return FollowUpService.updatePending(input)
}

export async function completePendingFollowUp(id: string): Promise<void> {
  await FollowUpService.complete({
    id,
    outcome: 'Completado',
    createNext: false,
    nextDate: null,
  })
}

export async function cancelPendingFollowUp(id: string): Promise<void> {
  await FollowUpService.cancel(id)
}

export async function createWorkflowActivity(
  prospectId: string,
  input: WorkflowActivityInput,
) {
  return WorkflowService.createActivity(prospectId, input)
}

export async function loadNextAction(prospectId: string) {
  return WorkflowService.loadNextAction(prospectId)
}

export async function loadDailyActionQueue() {
  return WorkflowService.loadDailyActionQueue()
}

export async function loadWorkflowHistory(prospectId: string) {
  return WorkflowService.history(prospectId)
}

export function isOverdue(value: string | null): boolean {
  if (!value) return false
  const date = new Date(value)
  return !Number.isNaN(date.getTime()) && date.getTime() < Date.now()
}

export function remainingTime(value: string | null): string {
  if (!value) return 'Sin fecha definida'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'Fecha no disponible'

  const difference = date.getTime() - Date.now()
  const minutes = Math.round(Math.abs(difference) / 60000)
  if (minutes < 1) return difference < 0 ? 'Vencida ahora' : 'Vence ahora'

  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  const duration = hours
    ? `${hours} h${remainingMinutes ? ` ${remainingMinutes} min` : ''}`
    : `${remainingMinutes} min`
  return difference < 0 ? `Vencida hace ${duration}` : `En ${duration}`
}

export async function getOrCreatePendingFollowUp(
  prospectId: string,
  profileId: string,
  scheduledAt: string,
  payload: { channel: string; reason?: string | null },
) {
  const result = await FollowUpService.schedule({
    prospectId,
    profileId,
    scheduledAt,
    channel: payload.channel,
    reason: payload.reason,
  })
  return {
    followUp: {
      id: result.followUpId,
      prospect_id: prospectId,
      assigned_to: profileId,
      scheduled_at: scheduledAt,
      status: 'pending',
    },
    reusedFollowUp: result.reusedFollowUp,
  }
}
